import React, { useEffect, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { useAsync } from "../hooks/use_async";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";
import { useConfig } from "./config";
import {
  Appearance,
  StripeElementLocale,
} from "@stripe/stripe-js/types/stripe-js/elements-group";
import { useI18n } from "../lib/i18n/i18n_provider";

interface StripeProps {
  children?: React.ReactNode;
}

export function StripeProvider(props: StripeProps) {
  const { children } = props;
  const config = useConfig();
  const { data: stripe, loading } = useAsync(
    () => loadStripe(config.stripeKey),
    [config.stripeKey]
  );

  if (loading || !stripe) {
    return null;
  }

  return <Elements stripe={stripe}>{children}</Elements>;
}

interface StripePropsV2 extends StripeProps {
  clientSecret?: string;
  partnerSlug?: string;
  stripePublishableKey?: string;
}

const generateAppearance = (): Appearance => {
  const style = getComputedStyle(document.documentElement);
  const colorText = style.getPropertyValue("--bs-body-color");
  const colorDanger = style.getPropertyValue("--bs-danger");
  const fontFamily = style.getPropertyValue("--text-font-family");
  const textDefaultFontFamily = style.getPropertyValue("--text-default-font");
  const colorBackground =
    style.getPropertyValue("--stripe-color-background") || "#fff";

  return {
    theme: "stripe",
    variables: {
      colorText: colorText,
      fontFamily: textDefaultFontFamily || fontFamily,
      fontSmooth: "always",
      colorTextPlaceholder: "#aab7c4",
      colorDanger: colorDanger,
      colorBackground,
    },
    // In order to make label style consistent,
    // we copy css from class .form-label.text-form-field-v2__label in file form_field.css
    rules: {
      ".Label": {
        fontWeight: "600",
        fontSize: "1rem",
        lineHeight: "1.2",
        color: "#6a6678",
        textTransform: "uppercase",
        marginBottom: "0.625rem",
      },
    },
  };
};

export function StripeProviderV2(props: StripePropsV2) {
  const { children, clientSecret, stripePublishableKey, partnerSlug } = props;
  const config = useConfig();
  const { locale } = useI18n();
  const [fontSource, setFontSource] = useState([]);

  useEffect(() => {
    if (partnerSlug) {
      import(`../offering/${partnerSlug}/${partnerSlug}_theme.tsx`).then(
        (config) => {
          setFontSource(config.embedStripeFontFamilySrc);
        }
      );
    }
  }, [partnerSlug]);

  const { data: stripe, loading } = useAsync(
    () => loadStripe(stripePublishableKey || config.stripeKey),
    [stripePublishableKey, config.stripeKey]
  );

  const appearance = generateAppearance();
  const options: StripeElementsOptions = {
    appearance,
    clientSecret,
    locale: locale.baseLanguageTag as StripeElementLocale,
    fonts: fontSource,
  };

  if (loading || !stripe) {
    return null;
  }

  return (
    <Elements stripe={stripe} options={options}>
      {children}
    </Elements>
  );
}
